"use client";

import Link from "next/link";
import { type ComponentProps } from "react";
import { twMerge } from "tailwind-merge";
import { SelectCreator, SelectSocial } from "../../db/schema";
import { formatCreatorName, formatServiceData, notNull } from "@drpcrd/utils";
import { CreatorAvatar } from "../creator/creator-avatar";
import { InstagramLogo, TikTokLogo } from "../icons";
import { Tooltip } from "../tooltip";

const MAX_CREATORS_IN_STACK = 5;

type CreatorAvatarSize = ComponentProps<typeof CreatorAvatar>["size"];

export type CreatorStackProps = {
  creators: (Pick<
    SelectCreator,
    "id" | "firstName" | "lastName" | "knownAs" | "avatar"
  > & {
    socials: Pick<SelectSocial, "avatar" | "service" | "username">[];
  })[];
  className?: string;
  avatarSize?: CreatorAvatarSize;
};

/**
 * Renders a compact stack of creator avatars with tooltip details and social links.
 */
export const CreatorStack = ({
  creators,
  className,
  avatarSize = "md",
}: CreatorStackProps) => (
  <div className={twMerge("flex items-center", className)}>
    <div className="mr-2 flex -space-x-2">
      {creators.slice(0, MAX_CREATORS_IN_STACK).map((creator) => {
        const socials = creator.socials
          .reduce(
            (uniqueSocials, social) => {
              if (!uniqueSocials.some((item) => item.service === social.service)) {
                uniqueSocials.push(social);
              }

              return uniqueSocials;
            },
            [] as typeof creator.socials,
          )
          .filter((service): service is NonNullable<typeof service> =>
            notNull(service),
          );

        return (
          <Tooltip
            key={creator.id}
            className="max-w-24 text-start"
            content={
              <>
                {formatCreatorName(creator)}
                {socials.map((social) => {
                  const serviceData = formatServiceData(
                    social.service,
                    social.username,
                  );

                  if (!serviceData) {
                    return null;
                  }

                  const { accountUrl, handle, title } = serviceData;
                  const IconComponent =
                    social.service === "instagram"
                      ? InstagramLogo
                      : social.service === "tiktok"
                        ? TikTokLogo
                        : null;

                  return (
                    <Link
                      href={accountUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="hover:text-primary mt-1 flex items-center text-xs"
                      key={title}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {IconComponent && (
                        <IconComponent className="mr-1 h-2 w-2 fill-white" />
                      )}
                      {handle}
                    </Link>
                  );
                })}
              </>
            }
          >
            <CreatorAvatar
              creatorSrc={creator.avatar}
              social1Src={creator.socials[0]?.avatar}
              social2Src={creator.socials[1]?.avatar}
              size={avatarSize}
            />
          </Tooltip>
        );
      })}
    </div>
    {creators.length > MAX_CREATORS_IN_STACK && (
      <span className="font-semibold">
        + {creators.length - MAX_CREATORS_IN_STACK} more
      </span>
    )}
  </div>
);
